import { FC } from 'react'
import { unstable_useBlocker as useBlocker } from 'react-router-dom'

import { CategoryBarSection, Widget, WidgetSection } from '@factorialco/f0-react/dist/experimental'
import { TimesheetInsightDocument, TimesheetInsightQuery } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import EstimatedTimesUtils from 'modules/attendance/lib/utils/EstimatedTimes'

const prepareData = (data: TimesheetInsightQuery) => {
  const today = moment()
  const currentMonth = today.format('MMMM').replace(/^\w/, (c) => c.toLocaleUpperCase())

  const plannedMinutes = EstimatedTimesUtils.sum(
    data?.attendance.employee?.attendanceEstimatedTimesConnection.nodes ?? []
  ).minutes

  const workedMinutes = data?.attendance.employee?.attendanceAggregatedWorkedTime?.minutes ?? 0
  const alertCount = data?.timeInsights.inconsistenciesConnection.totalCount ?? 0

  const getAlert = () => {
    if (alertCount === 0) return undefined
    if (alertCount === 1) return i18n.t('insights.widgets.timesheet.alert')
    return i18n.t('insights.widgets.timesheet.alerts', { count: alertCount })
  }

  const isOvertime = workedMinutes > plannedMinutes

  const [partial, total] = isOvertime
    ? [plannedMinutes, workedMinutes]
    : [workedMinutes, plannedMinutes]

  const differenceTitle = isOvertime
    ? i18n.t('insights.widgets.timesheet.overtime')
    : i18n.t('insights.widgets.timesheet.to_be_completed')

  const partialTitle = isOvertime
    ? i18n.t('insights.widgets.timesheet.planned')
    : i18n.t('insights.widgets.timesheet.worked')

  const difference = total - partial

  return {
    title: i18n.t('insights.widgets.timesheet.title'),
    subtitle: currentMonth,
    timesheet: {
      title: `${minutesToText(partial)}`,
      subtitle: `${isOvertime ? '+' : '-'}${minutesToText(difference)}`,
      data: [
        { name: partialTitle, value: partial },
        {
          name: differenceTitle,
          value: difference,
          color: isOvertime ? undefined : 'hsl(var(--neutral-30))',
        },
      ],
    },
    alert: getAlert(),
  }
}

type PreparedData = ReturnType<typeof prepareData>

const TimesheetWidgetComponent: FC<{
  data: PreparedData
  employeeId: string
  onClickWidget?: () => void
}> = ({ data: { title, subtitle, timesheet, alert }, employeeId, onClickWidget }) => {
  const { navigation } = useNavigation()

  const { employee } = useCurrentSession()
  const currentEmployeeId = employee?.id

  const today = moment()

  const commonLinkParams = {
    day: 1,
    periodType: 'monthly',
    year: today.year(),
    month: today.month() + 1,
  }

  const linkUrl =
    employeeId === currentEmployeeId
      ? navigation.clockIn.periodTypeYearMonthDay.fullPath(commonLinkParams)
      : navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
          employeeId,
          ...commonLinkParams,
        })

  const link = {
    title: i18n.t('insights.widgets.timesheet.title'),
    url: linkUrl,
  }

  useBlocker(({ nextLocation }) => {
    if (linkUrl.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  return (
    <Widget
      header={{
        title,
        subtitle,
        link,
      }}
      alert={alert}
    >
      <WidgetSection title={i18n.t('insights.widgets.timesheet.worked_planned_hours')}>
        <CategoryBarSection
          title={timesheet.title}
          subtitle={timesheet.subtitle}
          data={timesheet.data}
          legend
        />
      </WidgetSection>
    </Widget>
  )
}

export const buildTimesheetWidget = () =>
  buildWidget({
    id: 'timesheet',
    document: TimesheetInsightDocument,
    prepareData,
    component: TimesheetWidgetComponent,
    policies: ['seeAttendance'],
  })
